/**
 * Normaliza filas planas de BigQuery (resultado de LEFT JOIN) a objetos
 * de embarque con containers[] e items[] anidados.
 *
 * BigQuery retorna una fila por cada combinacion embarque-container o embarque-item.
 * Si un embarque tiene 3 containers, vienen 3 filas con datos de embarque repetidos.
 */

/** Prefijos de campos de container en la respuesta del API */
const CONTAINER_FIELD_PREFIX = 'container'

/**
 * Campos de container conocidos (empiezan con 'container' pero no son 'containerCount').
 * Es una base: normalizeTableRows recibe además los campos de contenedor de las
 * columnas visibles (visibleContainerApiFields), así un campo nuevo del backend
 * se des-anida sin tener que agregarlo acá. `containerFreeRetentionDate` llegaba
 * del API y se quedaba en el shipment plano porque esta lista no lo conocía:
 * la celda mostraba "-" con el dato ya en la respuesta.
 */
const CONTAINER_FIELDS = [
  'containerNumber', 'containerType', 'containerContainerStatus',
  'containerLicensePlate', 'containerDriverName', 'containerDriverIdentification',
  'containerLoadingAppointmentDate', 'containerWarehouseArrivalDate',
  'containerUnloadingDate', 'containerPortReturnDate', 'containerPortPickupDate',
  'containerRetentionCostCurrency', 'containerRetentionCostPerDay',
  'containerRetentionIncurredCost', 'containerRetentionIncurredDays',
  'containerStorageCostCurrency', 'containerStorageCostPerDay',
  'containerStorageIncurredCost', 'containerStorageIncurredDays',
  'containerFreeRetentionDate', 'containerFreeStorageDate',
  'containerVgm', 'containerVolume', 'containerUnitOfVolume', 'containerUnitOfWeight',
]

/** Campos que son de items en la respuesta del API */
const ITEM_FIELDS = [
  'sku', 'invoiceSupplier', 'quantity', 'invoiceNumber', 'description',
  'position', 'presentation', 'unitValue', 'currency', 'totalValue',
  'weight', 'weightUnit',
]

export interface NormalizedResult {
  shipments: any[]
  totalFlatRows: number
}

/**
 * Normaliza filas planas de BigQuery a objetos de embarque con subcollections.
 *
 * @param containerApiFields campos de contenedor pedidos al API (nombres con
 *   prefijo `container`), derivados de las columnas visibles. Se unen a la lista
 *   base para des-anidar también campos que esa lista no conoce.
 */
export function normalizeTableRows(
  rows: Record<string, any>[],
  containerApiFields: string[],
  hasItemFields: boolean,
  hasCustomsFields: boolean = false
): NormalizedResult {
  if (!rows || rows.length === 0) {
    return { shipments: [], totalFlatRows: 0 }
  }

  const totalFlatRows = rows.length
  const hasContainerFields = containerApiFields.length > 0
  const containerFields = new Set([...CONTAINER_FIELDS, ...containerApiFields])

  // Si no hay campos de container, items ni customs, no hay JOINs -> cada fila es un shipment
  if (!hasContainerFields && !hasItemFields && !hasCustomsFields) {
    return { shipments: rows.map(parseJsonFields), totalFlatRows }
  }

  // Agrupar filas por idNumber (ID del embarque)
  const shipmentMap = new Map<string, { shipment: Record<string, any>; containers: any[]; items: any[]; customsDeclarations: any[] }>()

  for (const row of rows) {
    const shipmentId = row.idNumber || row.id
    if (!shipmentId) continue

    if (!shipmentMap.has(shipmentId)) {
      // Extraer campos de embarque (excluir campos de container, item y customs)
      const shipmentData: Record<string, any> = {}
      for (const [key, value] of Object.entries(row)) {
        if (!containerFields.has(key) && !isItemField(key) && !isCustomsField(key)) {
          shipmentData[key] = value
        }
      }
      shipmentMap.set(shipmentId, { shipment: shipmentData, containers: [], items: [], customsDeclarations: [] })
    }

    const entry = shipmentMap.get(shipmentId)!

    // Extraer datos de container de esta fila
    if (hasContainerFields) {
      const containerData = extractContainerData(row, containerFields)
      if (containerData && hasNonNullValues(containerData)) {
        // Evitar duplicados por containerNumber
        const isDuplicate = entry.containers.some(
          (c: any) => c.number && c.number === containerData.number
        )
        if (!isDuplicate) {
          entry.containers.push(containerData)
        }
      }
    }

    // Extraer datos de item de esta fila
    if (hasItemFields) {
      const itemData = extractItemData(row)
      if (itemData && hasNonNullValues(itemData)) {
        entry.items.push(itemData)
      }
    }

    // Extraer datos de customs de esta fila
    if (hasCustomsFields) {
      const customsData = extractCustomsData(row)
      if (customsData && hasNonNullValues(customsData)) {
        const isDuplicate = entry.customsDeclarations.some(
          (c: any) => c.declarationNumber && c.declarationNumber === customsData.declarationNumber
        )
        if (!isDuplicate) {
          entry.customsDeclarations.push(customsData)
        }
      }
    }
  }

  // Convertir el Map a array de shipments con containers/items/customsDeclarations anidados
  const shipments = Array.from(shipmentMap.values()).map(({ shipment, containers, items, customsDeclarations }) => ({
    ...parseJsonFields(shipment),
    containers,
    items,
    customsDeclarations,
  }))

  return { shipments, totalFlatRows }
}

/**
 * Extrae campos de container de una fila y los mapea al formato interno.
 * API: containerNumber -> interno: number
 * API: containerType -> interno: type
 * API: containerXxx -> interno: xxx (primera letra en minuscula)
 */
function extractContainerData(row: Record<string, any>, containerFields: Set<string>): Record<string, any> | null {
  const data: Record<string, any> = {}
  let hasData = false

  for (const field of containerFields) {
    if (field in row && row[field] !== undefined) {
      const internalKey = mapContainerFieldToInternal(field)
      data[internalKey] = row[field]
      hasData = true
    }
  }

  // containerContainerCount del modo agrupado (GROUP BY containerType)
  if ('containerContainerCount' in row && row.containerContainerCount !== undefined) {
    data.containerCount = row.containerContainerCount
    hasData = true
  }

  return hasData ? data : null
}

/**
 * Mapea nombre de campo container del API al formato interno.
 * containerNumber -> number
 * containerType -> type
 * containerXxx -> xxx
 */
function mapContainerFieldToInternal(apiField: string): string {
  if (apiField === 'containerNumber') return 'number'
  if (apiField === 'containerType') return 'type'
  if (apiField === 'containerContainerStatus') return 'containerStatus'

  // Quitar prefijo 'container' y hacer lowercase la primera letra
  const withoutPrefix = apiField.replace(CONTAINER_FIELD_PREFIX, '')
  return withoutPrefix.charAt(0).toLowerCase() + withoutPrefix.slice(1)
}

/**
 * Extrae campos de item de una fila.
 */
function extractItemData(row: Record<string, any>): Record<string, any> | null {
  const data: Record<string, any> = {}
  let hasData = false

  for (const field of ITEM_FIELDS) {
    if (field in row && row[field] !== undefined) {
      data[field] = row[field]
      hasData = true
    }
  }

  return hasData ? data : null
}

/** Prefijo de campos de customs en la respuesta del API */
const CUSTOMS_FIELD_PREFIX = 'customs'

/** Campos de customs que en Firestore ya tienen el prefijo 'customs' */
const CUSTOMS_FIELDS_WITH_PREFIX = new Set(['customsDutyValue'])

/**
 * Extrae campos de customs de una fila y les quita el prefijo.
 * API: customsDeposit -> interno: deposit
 * API: customsManifestNumber -> interno: manifestNumber
 * API: customsDutyValue -> interno: customsDutyValue (ya tenía prefijo en Firestore)
 */
function extractCustomsData(row: Record<string, any>): Record<string, any> | null {
  const data: Record<string, any> = {}
  let hasData = false

  for (const [key, value] of Object.entries(row)) {
    if (isCustomsField(key) && value !== undefined) {
      const finalKey = CUSTOMS_FIELDS_WITH_PREFIX.has(key)
        ? key
        : (() => {
            const withoutPrefix = key.replace(CUSTOMS_FIELD_PREFIX, '')
            return withoutPrefix.charAt(0).toLowerCase() + withoutPrefix.slice(1)
          })()
      data[finalKey] = value
      hasData = true
    }
  }

  return hasData ? data : null
}

function isCustomsField(key: string): boolean {
  return key.startsWith(CUSTOMS_FIELD_PREFIX) && key !== 'customsDetail'
}

function isItemField(key: string): boolean {
  return ITEM_FIELDS.includes(key)
}

function hasNonNullValues(obj: Record<string, any>): boolean {
  return Object.values(obj).some(v => v !== null && v !== undefined && v !== '')
}

function toBooleanField(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return value.toLowerCase() === 'true'
  return false
}

const JSON_FIELDS = ['houses']

function parseJsonFields(row: Record<string, any>): Record<string, any> {
  const result = { ...row }
  for (const field of JSON_FIELDS) {
    if (typeof result[field] === 'string') {
      try { result[field] = JSON.parse(result[field]) } catch { /* keep as-is */ }
    }
  }
  if (!result.consolidationType) {
    result.consolidationType = 'standalone'
  }
  result.isTracking = toBooleanField(result.isTracking)
  return result
}
